import React, { useState, useEffect } from 'react'
import { PET_TEMPERAMENTS, PET_TYPES } from '@cobasi/cobasi-utils/constants'
import { getTemperamentLabel, getTypeLabel } from '@cobasi/cobasi-utils/helpers/pet'

import { FormButton } from 'components/button'
import { Radio, Input } from 'components/forms'
import Select from 'components/forms/V2/select'
import * as S from './styles'

import { updatePet, getBreeds } from '../../services/petProfileService'
import { scrollTop } from '../../utils'
import { usePetAndServicesContext } from '../../context/petAndServicesContext'

const validate = ({ type, breed, weight, gender, castrated, temperament }) => {
  const errors = {}
  if (!gender) {
    errors.gender = 'Informe o sexo do Pet'
  }

  if (typeof castrated !== 'boolean') {
    errors.castrated = 'Informe se o Pet é castrado'
  }

  if (!temperament) {
    errors.temperament = 'Preencha o temperamento do Pet'
  }

  if (!weight || (weight < 1 || weight > 99)) {
    errors.weight = 'Informe o peso do seu Pet (1 a 99 kgs)'
  }

  if (!type) {
    errors.type = 'Informe a espécie do Pet'
  }

  if (!breed) {
    errors.breed = 'Informe a raça do Pet'
  }

  return errors
}

const temperamentsList = Object.values(PET_TEMPERAMENTS).map(temp => ({ value: temp, label: getTemperamentLabel(temp) }))
const petTypeList = Object.values(PET_TYPES).map(type => ({ value: type, label: getTypeLabel(type) }))

const IncompletePetDataForm = ({ pet, onSuccess }) => {
  const { functions } = usePetAndServicesContext()
  const { handleSelectedMyPet } = functions
  const [castrated, setCastrated] = useState(pet.castrated)
  const [temperament, setTemperament] = useState(pet.temperament)
  const [gender, setGender] = useState(pet.gender)
  const [weight, setWeight] = useState(pet.weight)
  const [breed, setBreed] = useState(pet.breed?.id || null)
  const [type, setType] = useState(pet.breed?.type || null)
  const [petBreedsList, setPetBreedsList] = useState([])

  const [errors, setErrors] = useState({})
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleSubmit = async () => {
    const errors = validate({ type, breed, weight, gender, castrated, temperament })
    if (Object.keys(errors).length) {
      setErrors(errors)
    } else {
      setErrors({})

      try {
        setIsSubmitting(true)
        const petUpdated = await updatePet({ id: pet.id, breed, weight, gender, castrated, temperament })
        handleSelectedMyPet(petUpdated)
        scrollTop('auto')
        await onSuccess()
      } catch (err) {
        setErrors({ ...errors, submit: 'Falha ao atualizar dados de Pet.' })
      } finally {
        setIsSubmitting(false)
      }
    }
  }

  useEffect(() => {
    const fetchBreeds = async () => {
      const response = await getBreeds(type)
      const formattedBreeds = response.map(({ id, name }) => ({ value: id, label: name }))
      setPetBreedsList(formattedBreeds)
    }

    if (!pet.breed?.id && !pet.breed?.type) {
      setBreed(null)
      fetchBreeds()
    }
  }, [type])

  return (
    <S.FormContainer>
      <p>
        Os dados do Pet estão incompletos. Por favor, preencha as informações que estão faltando para seguir com o agendamento.
      </p>

      {!pet?.breed?.type && (
        <S.FieldContent>
          <S.Field>
            <Select
              onChange={(value) => setType(value)}
              selectedValue={type}
              label='Espécie'
              emptyLabel='Selecione a espécie do seu pet'
              height={48}
              options={petTypeList}
            />
          </S.Field>
          {errors.type && <S.Error>{errors.type}</S.Error>}
        </S.FieldContent>
      )}

      {!pet?.breed && (
        <S.FieldContent>
          <S.Field>
            <Select
              key={breed}
              onChange={(value) => setBreed(value)}
              selectedValue={breed}
              label='Raça'
              emptyLabel='Selecione a raça do seu pet'
              height={48}
              options={petBreedsList}
              disabled={!type}
            />
          </S.Field>
          {errors.breed && <S.Error>{errors.breed}</S.Error>}
        </S.FieldContent>
      )}

      {!pet?.weight && (
        <S.FieldContent>
          <S.Field>
            <Input
              placeholder='Digite o peso do seu pet'
              label='Peso'
              name='weight'
              type='number'
              onChange={(event) => setWeight(event.target.value)}
              defaultValue={weight}
              margin='0px'
              height='40px'
              borderRadius='8px'
              padding='16px 12px'
            />
          </S.Field>
          {errors.weight && <S.Error>{errors.weight}</S.Error>}
        </S.FieldContent>
      )}

      {!pet?.gender && (
        <S.FieldContent>
          <S.Field>
            <S.Label>Sexo</S.Label>
            <S.RadioContainer>
              <Radio
                name='gender'
                label='Fêmea'
                onChange={() => setGender('FEMALE')}
                checked={gender === 'FEMALE'}
              />
              <Radio
                name='gender'
                label='Macho'
                onChange={() => setGender('MALE')}
                checked={gender === 'MALE'}
              />
            </S.RadioContainer>
          </S.Field>
          {errors.gender && <S.Error>{errors.gender}</S.Error>}
        </S.FieldContent>
      )}

      {typeof pet?.castrated !== 'boolean' && (
        <S.FieldContent>
          <S.Field>
            <S.Label>É castrado?</S.Label>
            <S.RadioContainer>
              <Radio
                name='castrated'
                label='Não'
                onChange={() => setCastrated(false)}
                checked={castrated === false}
              />
              <Radio
                name='castrated'
                label='Sim'
                onChange={() => setCastrated(true)}
                checked={castrated === true}
              />
            </S.RadioContainer>
          </S.Field>
          {errors.castrated && <S.Error>{errors.castrated}</S.Error>}
        </S.FieldContent>
      )}

      {!pet?.temperament && (
        <S.FieldContent>
          <S.Field>
            <S.Label>Temperamento</S.Label>
            <S.RadioContainer>
              {temperamentsList.map((temp) => (
                <Radio
                  key={temp.value}
                  name='temperament'
                  label={temp.label}
                  onChange={() => setTemperament(temp.value)}
                  checked={temperament === temp.value}
                />
              ))}
            </S.RadioContainer>
          </S.Field>
          {errors.temperament && <S.Error>{errors.temperament}</S.Error>}
        </S.FieldContent>
      )}

      <S.BottomPositioning>
        <FormButton margin='0' label='Salvar dados' onClick={handleSubmit} disabled={isSubmitting} />
        {errors.submit && <S.Error>{errors.submit}</S.Error>}
      </S.BottomPositioning>
    </S.FormContainer>
  )
}

export default IncompletePetDataForm
